import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import type { Address } from "viem";
import config, { SERVER_URL } from "@/config/config";
import { formatEther, getPublicClient } from "@/helper/viem";
import { VaultHistory } from "@/types/Vault";

export interface VaultUser {
  name: string;
  worth: number;
  worthPoints: number;
  premiumStatus: string;
  familyName: string;
  payoutPercent: number;
}

export interface VaultFamily {
  id: number;
  name: string;
  logo: string;
  activeMembersWorthSum: number;
  activeMemberCount: number;
  totalPayoutAmount: number;
}

export interface PayoutHistoryRecord {
  id: number;
  userId: number;
  name: string;
  address: string;
  payout: number;
  actualPayoutAmount: number;
  timestamp: number;
}

export interface PayoutHistoryResponse {
  roundId: string;
  count: number;
  records: PayoutHistoryRecord[];
}

export interface VaultState {
  vaultBalance: number;
  loadingVaultBalance: boolean;
  history: VaultHistory;
  loadingHistory: boolean;
  users: VaultUser[];
  loadingUsers: boolean;
  totalUsers: number;
  families: VaultFamily[];
  loadingFamilies: boolean;
  payoutHistory: PayoutHistoryRecord[];
  loadingPayoutHistory: boolean;
  totalPayoutHistory: number;
  selectedRoundId: string | null;
}

const initialState: VaultState = {
  vaultBalance: 0,
  loadingVaultBalance: false,
  history: {
    tax: [],
    key: [],
    market: [],
    slot: [],
    perkSell: [],
    timestamp: [],
  },
  loadingHistory: false,
  users: [],
  loadingUsers: false,
  totalUsers: 0,
  families: [],
  loadingFamilies: false,
  payoutHistory: [],
  loadingPayoutHistory: false,
  totalPayoutHistory: 0,
  selectedRoundId: null,
};

export const fetchVaultBalance = createAsyncThunk(
  "vault/fetchVaultBalance",
  async () => {
    const publicClient = getPublicClient();
    const [balanceWei1, balanceWei2] = await Promise.all([
      publicClient.getBalance({ address: config.vaultAddress as Address }),
      publicClient.getBalance({ address: config.vault1Address as Address }),
    ]);

    const totalBalance =
      parseFloat(formatEther(balanceWei1)) + parseFloat(formatEther(balanceWei2));

    return parseFloat(totalBalance.toFixed(2));
  }
);
export const getVaultHistory = createAsyncThunk(
  "vault/getVaultHistory",
  async () => {
    const res = await axios.get(`${SERVER_URL}/vault/history`);
    return res.data.result as VaultHistory;
  }
);

export const getVaultUsers = createAsyncThunk(
  "vault/getVaultUsers",
  async ({ from, count }: { from: number; count: number }) => {
    const res = await axios.get(`${SERVER_URL}/vault/users`, {
      params: { from, count },
    });
    // Handle different response formats
    if (Array.isArray(res.data)) {
      return {
        users: res.data,
        total: res.data.length,
      };
    }
    return {
      users: res.data.users || [],
      total: res.data.total || res.data.count || 0,
    };
  }
);

export const getVaultFamilies = createAsyncThunk(
  "vault/getVaultFamilies",
  async () => {
    const res = await axios.get(`${SERVER_URL}/vault/families`);
    return res.data as VaultFamily[];
  }
);

export const getPayoutHistory = createAsyncThunk(
  "vault/getPayoutHistory",
  async (roundId: string) => {
    const res = await axios.get(`${SERVER_URL}/vault/payout-history`, {
      params: { roundId },
    });
    return res.data as PayoutHistoryResponse;
  }
);

export const vaultSlice = createSlice({
  name: "vault",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchVaultBalance.pending, (state) => {
      state.loadingVaultBalance = true;
    });
    builder.addCase(fetchVaultBalance.fulfilled, (state, { payload }) => {
      state.loadingVaultBalance = false;
      state.vaultBalance = payload;
    });
    builder.addCase(fetchVaultBalance.rejected, (state) => {
      state.loadingVaultBalance = false;
    });

    builder.addCase(getVaultHistory.pending, (state) => {
      state.loadingHistory = true;
    });
    builder.addCase(getVaultHistory.fulfilled, (state, { payload }) => {
      state.loadingHistory = false;
      state.history = payload;
    });
    builder.addCase(getVaultHistory.rejected, (state, { error }) => {
      state.loadingHistory = false;
      console.log(error);
    });

    builder.addCase(getVaultUsers.pending, (state) => {
      state.loadingUsers = true;
    });
    builder.addCase(getVaultUsers.fulfilled, (state, { payload }) => {
      state.loadingUsers = false;
      state.users = payload.users;
      state.totalUsers = payload.total;
    });
    builder.addCase(getVaultUsers.rejected, (state, { error }) => {
      state.loadingUsers = false;
      console.log(error);
    });

    builder.addCase(getVaultFamilies.pending, (state) => {
      state.loadingFamilies = true;
    });
    builder.addCase(getVaultFamilies.fulfilled, (state, { payload }) => {
      state.loadingFamilies = false;
      state.families = payload;
    });
    builder.addCase(getVaultFamilies.rejected, (state, { error }) => {
      state.loadingFamilies = false;
      console.log(error);
    });

    builder.addCase(getPayoutHistory.pending, (state) => {
      state.loadingPayoutHistory = true;
    });
    builder.addCase(getPayoutHistory.fulfilled, (state, { payload }) => {
      state.loadingPayoutHistory = false;
      state.payoutHistory = payload.records;
      state.totalPayoutHistory = payload.count;
      state.selectedRoundId = payload.roundId;
    });
    builder.addCase(getPayoutHistory.rejected, (state, { error }) => {
      state.loadingPayoutHistory = false;
      console.log(error);
    });
  },
});

export default vaultSlice.reducer;
